/**
 * Primitive-layer schema builder (Zod 4).
 *
 * Turns `FieldDescriptor[]` into a Zod object schema for RHF's `zodResolver`.
 * Knows NOTHING about workflows — it validates a countries dropdown and a
 * workflow page through identical code. Client-side only (length / regex /
 * required / in-options); server validation stays authoritative.
 */
import { z, ZodType } from "zod";
import {
  FieldConstraints,
  FieldDescriptor,
  FieldKind,
  FieldOption,
} from "../../types/components/dynamicForms";

const REQUIRED_MESSAGE = "This field is required.";
const COUNTRY_CODE_REQUIRED_MESSAGE = "Select a country code.";

/**
 * Error map for a schema's own type check: a missing value (undefined — e.g. a
 * phone with a country but no number yet) gets a user-facing message instead of
 * Zod's "expected string, received undefined". Anything else falls through to
 * the default / check-specific message.
 */
const missingValue = (message: string) => (issue: { input?: unknown }) =>
  issue.input === undefined || issue.input === null ? message : undefined;

/** Base string schema with a friendly missing-value message. */
const requiredString = (): z.ZodString =>
  z.string({ error: missingValue(REQUIRED_MESSAGE) });

/** Apply length + pattern to a base string schema. Requiredness handled separately. */
function applyStringConstraints(
  base: z.ZodString,
  c: FieldConstraints | undefined,
  label: string
): z.ZodString {
  let s = base;
  if (c && typeof c.min === "number") {
    s = s.min(c.min, `${label} must be at least ${c.min} characters.`);
  }
  if (c && typeof c.max === "number") {
    s = s.max(c.max, `${label} must be at most ${c.max} characters.`);
  }
  if (c && c.pattern) {
    s = s.regex(new RegExp(c.pattern), `${label} is invalid.`);
  }
  return s;
}

/**
 * Enforce requiredness for a string-backed kind. The server sends required
 * fields with min: 0 (fromWorkflowField copies min verbatim), so requiredness
 * CANNOT be left to min — a required field without an effective min >= 1 must
 * still reject "" and whitespace-only. Applied via refine so the stored value
 * is not silently trimmed.
 */
function requireNonEmptyString(
  schema: z.ZodString,
  c: FieldConstraints | undefined,
  label: string,
  required: boolean
): ZodType {
  const hasEffectiveMin = typeof c?.min === "number" && c.min >= 1;
  if (required && !hasEffectiveMin) {
    return schema.refine((v) => v.trim().length > 0, {
      message: REQUIRED_MESSAGE,
    });
  }
  return schema;
}

/**
 * A select is a non-empty code refined against its options. When options are
 * empty (e.g. an unresolved cascade), we accept any non-empty string — the
 * server remains authoritative and the schema is rebuilt once options resolve.
 */
function buildSelectSchema(
  options: FieldOption[] | undefined,
  label: string
): ZodType {
  const values = (options ?? []).map((o) => o.value);
  let s: ZodType = requiredString().min(1, REQUIRED_MESSAGE);
  if (values.length > 0) {
    s = (s as z.ZodString).refine((v) => values.includes(v), {
      message: `Select a valid option for ${label}.`,
    });
  }
  return s;
}

/** Compound phone: validate `number`; require `country`; tolerate stray `type`. */
function buildPhoneSchema(
  c: FieldConstraints | undefined,
  label: string,
  required: boolean
): ZodType {
  // Number gets its constraints, then a required non-empty guard (min: 0 from
  // the server must not make a required phone accept a blank number).
  const number = requireNonEmptyString(
    applyStringConstraints(requiredString(), c, `${label} number`),
    c,
    `${label} number`,
    required
  );
  const country = z.looseObject(
    {
      id: z.number(),
      code: z.string(),
      name: z.string(),
      phoneCode: z.string(),
      iso: z.string(),
      numericCode: z.string().nullable().optional(),
      isoCode: z.string().optional(),
    },
    { error: missingValue(COUNTRY_CODE_REQUIRED_MESSAGE) }
  );
  return z.looseObject(
    { number, country },
    { error: missingValue(REQUIRED_MESSAGE) }
  );
}

/**
 * Apply numeric value bounds to a string-backed numeric schema. Distinct from
 * min/max, which are character-length bounds. Empty string is left to the
 * required/optional layer — a bound only judges a present, parseable number.
 */
function applyNumericBounds(
  schema: ZodType,
  c: FieldConstraints | undefined,
  label: string
): ZodType {
  if (!c || (typeof c.minValue !== "number" && typeof c.maxValue !== "number")) {
    return schema;
  }
  return schema.superRefine((raw, ctx) => {
    const value = (raw as string).trim();
    if (value === "") return;
    const n = Number(value);
    if (Number.isNaN(n)) return; // digit/decimal regex already reported the shape
    if (typeof c.minValue === "number" && n < c.minValue) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          c.minValueMessage ?? `${label} must be at least ${c.minValue}.`,
      });
    }
    if (typeof c.maxValue === "number" && n > c.maxValue) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          c.maxValueMessage ?? `${label} must be at most ${c.maxValue}.`,
      });
    }
  });
}

/** Build the base schema for one descriptor (optional-wrapping applied separately). */
function buildBase(field: FieldDescriptor): ZodType {
  const label = field.label ?? field.name;
  const kind: FieldKind = field.kind;
  const c = field.constraints;
  const required = c?.required ?? false;
  switch (kind) {
    case "text":
      return requireNonEmptyString(
        applyStringConstraints(requiredString(), c, label), c, label, required
      );
    case "email":
      // server-owned pattern via constraints.pattern; no z.email()
      return requireNonEmptyString(
        applyStringConstraints(requiredString(), c, label), c, label, required
      );
    case "integer": {
      const base = applyStringConstraints(requiredString(), c, label);
      const withDigits = c?.pattern
        ? base
        : base.regex(/^\d*$/, `${label} must be a whole number.`);
      return applyNumericBounds(
        requireNonEmptyString(withDigits, c, label, required),
        c,
        label
      );
    }
    case "decimal": {
      const base = applyStringConstraints(requiredString(), c, label);
      const withDecimal = c?.pattern
        ? base
        : base.regex(/^\d*\.?\d*$/, `${label} must be a number.`);
      return applyNumericBounds(
        requireNonEmptyString(withDecimal, c, label, required),
        c,
        label
      );
    }
    case "phone":
      return buildPhoneSchema(c, label, required);
    case "select":
      // buildSelectSchema always enforces non-empty; requiredness handled by
      // the optional-wrapping step (an optional select tolerates "").
      return buildSelectSchema(field.options, label);
    default: {
      const _exhaustive: never = kind;
      throw new Error(`Unhandled field kind: ${String(_exhaustive)}`);
    }
  }
}

/** Optional fields tolerate an absent key or an empty string; present values still validate. */
function applyRequiredness(schema: ZodType, required: boolean): ZodType {
  if (required) return schema;
  return z.union([schema, z.literal("")]).optional();
}

/**
 * Build a Zod object schema keyed by each descriptor's `name`.
 * This is the single validation entry point for the whole engine.
 */
export function buildZodSchema(
  fields: FieldDescriptor[]
): z.ZodObject<Record<string, ZodType>> {
  const shape: Record<string, ZodType> = {};
  for (const field of fields) {
    // Read-only values are server-owned and uneditable — never block submit on them.
    if (field.readOnly) {
      shape[field.name] = z.unknown();
      continue;
    }
    shape[field.name] = applyRequiredness(
      buildBase(field),
      field.constraints?.required ?? false
    );
  }
  return z.object(shape);
}